/**
 * SaveMealDialog — prompts for a meal name then POSTs to /me/saved-meals.
 * ED-safe placeholder copy; no diet-shaming language.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateSavedMeal } from '../hooks/useSavedMeals.js';

interface SaveMealDialogProps {
  open: boolean;
  entryIds: string[];
  onClose: () => void;
  onSaved: (mealName: string) => void;
}

export function SaveMealDialog({ open, entryIds, onClose, onSaved }: SaveMealDialogProps) {
  const { t } = useTranslation(['log', 'settings']);
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const create = useCreateSavedMeal();

  const resetCreate = create.reset;
  useEffect(() => {
    if (open) {
      setName('');
      resetCreate();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, resetCreate]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || entryIds.length === 0) return;
    await create.mutateAsync({ name: trimmed, fromEntryIds: entryIds });
    onSaved(trimmed);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('log:quickLog.saveMealTitle')}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-shell">
        <h2 className="mb-3 text-base font-semibold text-ink dark:text-ink">
          {t('log:quickLog.saveMealTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('log:quickLog.saveMealPlaceholder')}
            maxLength={120}
            required
            className="
              rounded-lg border border-ink/20 px-3 py-2 text-sm text-ink placeholder-neutral-400
              focus:outline-none focus:ring-2 focus:ring-leaf
              dark:border-ink/20 dark:bg-shell dark:text-ink dark:placeholder-neutral-500
            "
          />

          {create.isError && (
            <p className="text-xs text-clay dark:text-clay" role="alert">
              Could not save. Please try again.
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={!name.trim() || create.isPending}
              className="
                flex-1 rounded-xl bg-leaf px-4 py-2.5 text-sm font-semibold text-white
                hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              {create.isPending ? 'Saving…' : t('log:quickLog.saveMealButton')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-husk px-4 py-2.5 text-sm font-medium text-ink hover:bg-husk/30 dark:border-ink/20 dark:text-ink"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
